import { useQuery } from "@tanstack/react-query";
import React from "react";
import { organizationAboutUser } from "../../../services/organizationService";

const CompanyAboutTab = ({ slug }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["company-about", slug],
    queryFn: async () => {
      if (!slug) return null;
      return await organizationAboutUser({ slug });
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Loading company information...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 text-sm text-red-500">
        Failed to load company information.
      </div>
    );
  }

  const aboutData = data?.data;

  if (!aboutData) {
    return (
      <div className="p-6 text-sm text-gray-500">
        No company data available.
      </div>
    );
  }

  /* ================= Extract & Format Data Safely ================= */

  const description = aboutData?.content?.en?.content?.trim() || null;

  const website = aboutData?.website?.trim() || null;

  const formattedWebsite =
    website && !website.startsWith("http") ? `https://${website}` : website;

  const contactEmail = aboutData?.contactEmail || null;

  const headquarter = aboutData?.headquarter || null;

  const createdYear = aboutData?.createdAt
    ? new Date(aboutData.createdAt).getFullYear()
    : null;

  const businessInfo = aboutData?.businessInfo || {};

  const industry = businessInfo?.industry || null;
  const employeeCount = businessInfo?.employeeCount || null;
  const registrationNumber = businessInfo?.registrationNumber || null;
  const taxId = businessInfo?.taxId || null;
  const vatNumber = businessInfo?.vatNumber || null;

  /* ================= UI ================= */

  return (
    <div className="p-6 border rounded-lg bg-white shadow-sm">
      {/* Overview Section */}
      <h3 className="text-lg font-semibold text-gray-900">Overview</h3>

      <div className="mt-4 text-gray-600 text-sm leading-relaxed">
        {description ? (
          <p>{description}</p>
        ) : (
          <p className="text-gray-400">No company description available.</p>
        )}
      </div>

      {/* Company Details Section */}
      <div className="mt-8 border-t pt-6 grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12 text-sm">
        {industry && <InfoItem label="Industry" value={industry} />}

        {employeeCount && (
          <InfoItem label="Company Size" value={`${employeeCount} employees`} />
        )}

        {headquarter && <InfoItem label="Headquarters" value={headquarter} />}

        {createdYear && <InfoItem label="Founded" value={createdYear} />}

        {registrationNumber && (
          <InfoItem label="Registration Number" value={registrationNumber} />
        )}

        {taxId && <InfoItem label="Tax ID" value={taxId} />}

        {vatNumber && <InfoItem label="VAT Number" value={vatNumber} />}

        {contactEmail && (
          <div>
            <p className="font-medium text-gray-800">Contact Email</p>
            <a
              href={`mailto:${contactEmail}`}
              className="text-blue-600 hover:underline break-all"
            >
              {contactEmail}
            </a>
          </div>
        )}

        {formattedWebsite && (
          <div>
            <p className="font-medium text-gray-800">Website</p>
            <a
              href={formattedWebsite}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              {website}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

/* ================= Reusable Info Item ================= */

const InfoItem = ({ label, value }) => (
  <div>
    <p className="font-medium text-gray-800">{label}</p>
    <p className="text-gray-600">{value}</p>
  </div>
);

export default CompanyAboutTab;
